import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ShelterUpdate } from "@/types/shelterUpdate";
import type { Shelter } from "@/types/shelter";

export interface ShelterUpdateWithOriginal extends ShelterUpdate {
  originalShelter?: Shelter;
}

// Helper to normalize values for comparison (null, undefined, "" are equivalent)
const normalizeValue = (v: any) => {
  if (v === null || v === undefined || v === "") return null;
  return v;
};

// Check if a value has changed
const hasChanged = (oldVal: any, newVal: any): boolean => {
  return normalizeValue(oldVal) !== normalizeValue(newVal);
};

// Build an object with only the changed fields
const getChangedFields = (
  original: Shelter | undefined,
  update: ShelterUpdate,
  options?: {
    /**
     * When provided, only these update keys are considered as intentionally edited.
     * This prevents overwriting fields due to stale form payloads or out-of-order approvals.
     */
    allowUpdateKeys?: Array<keyof ShelterUpdate>;
  }
): Record<string, any> => {
  const changes: Record<string, any> = {};

  // Map update fields to shelter fields
  const fieldMappings: Array<{
    updateKey: keyof ShelterUpdate;
    shelterKey: keyof Shelter;
  }> = [
    { updateKey: "shelter_name", shelterKey: "name" },
    { updateKey: "city", shelterKey: "city" },
    { updateKey: "region", shelterKey: "region" },
    { updateKey: "address", shelterKey: "address" },
    { updateKey: "postal_code", shelterKey: "postal_code" },
    { updateKey: "lat", shelterKey: "lat" },
    { updateKey: "lng", shelterKey: "lng" },
    { updateKey: "phone", shelterKey: "phone" },
    { updateKey: "email", shelterKey: "email" },
    { updateKey: "website", shelterKey: "website" },
    { updateKey: "current_needs", shelterKey: "current_needs" },
    { updateKey: "urgent_needs", shelterKey: "urgent_needs" },
    { updateKey: "dog_walking_available", shelterKey: "dog_walking_available" },
    { updateKey: "dogs_available", shelterKey: "dogs_available" },
    { updateKey: "cats_available", shelterKey: "cats_available" },
    { updateKey: "volunteering_available", shelterKey: "volunteering_available" },
    { updateKey: "volunteer_info", shelterKey: "volunteer_info" },
    { updateKey: "description", shelterKey: "description" },
  ];

  for (const { updateKey, shelterKey } of fieldMappings) {
    if (options?.allowUpdateKeys && !options.allowUpdateKeys.includes(updateKey)) {
      continue;
    }

    const updateValue = update[updateKey];
    const originalValue = original?.[shelterKey];

    // For new shelters (no original), include all non-null values
    if (!original) {
      if (normalizeValue(updateValue) !== null) {
        changes[shelterKey] = updateValue;
      }
    } else {
      // For existing shelters, only include changed fields
      if (hasChanged(originalValue, updateValue)) {
        changes[shelterKey] = updateValue;
      }
    }
  }

  return changes;
};

const getAllowUpdateKeysFromPending = (update: ShelterUpdate): Array<keyof ShelterUpdate> | undefined => {
  const raw = (update.original_values as any)?.__changed_fields;
  if (!Array.isArray(raw)) return undefined;

  // Whitelist keys we support in diffs to avoid accidental injection/typos
  const allowed = new Set<keyof ShelterUpdate>([
    "shelter_name",
    "city",
    "region",
    "address",
    "postal_code",
    "lat",
    "lng",
    "phone",
    "email",
    "website",
    "current_needs",
    "urgent_needs",
    "dog_walking_available",
    "dogs_available",
    "cats_available",
    "volunteering_available",
    "volunteer_info",
    "description",
  ]);

  return raw.filter((k: unknown): k is keyof ShelterUpdate => typeof k === "string" && allowed.has(k as any));
};

export const useShelterUpdates = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["shelter-updates"],
    queryFn: async (): Promise<ShelterUpdateWithOriginal[]> => {
      // Get current session to ensure we have auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return [];
      }

      const { data: updates, error } = await supabase
        .from("shelter_updates")
        .select("*")
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching shelter updates:", error);
        throw new Error(error.message);
      }

      if (!updates || updates.length === 0) {
        return [];
      }

      // Get unique shelter IDs that are not null
      const shelterIds = [...new Set(
        updates
          .filter((u) => u.shelter_id !== null)
          .map((u) => u.shelter_id as string)
      )];

      // Fetch original shelters
      let sheltersMap: Record<string, Shelter> = {};
      if (shelterIds.length > 0) {
        const { data: shelters, error: sheltersError } = await supabase
          .from("shelters")
          .select("*")
          .in("id", shelterIds);

        if (!sheltersError && shelters) {
          sheltersMap = shelters.reduce((acc, s) => {
            acc[s.id] = {
              id: s.id,
              name: s.name,
              city: s.city,
              region: s.region,
              address: s.address,
              postal_code: (s as any).postal_code ?? null,
              lat: s.lat,
              lng: s.lng,
              phone: s.phone,
              email: s.email,
              website: s.website,
              current_needs: s.current_needs,
              urgent_needs: s.urgent_needs,
              urgent_needs_set_at: s.urgent_needs_set_at,
              dog_walking_available: s.dog_walking_available,
              volunteering_available: s.volunteering_available,
              dogs_available: s.dogs_available,
              cats_available: s.cats_available,
              volunteer_info: s.volunteer_info,
              description: s.description,
              last_updated: s.last_updated ?? s.created_at,
              created_at: s.created_at,
              data_verified: s.data_verified ?? false,
              organization_verified: (s as any).organization_verified ?? false,
              organization_verified_at: (s as any).organization_verified_at ?? null,
              organization_verification_pending: (s as any).organization_verification_pending ?? false,
              organization_verification_requested_at: (s as any).organization_verification_requested_at ?? null,
              contacted: (s as any).contacted ?? false,
            };
            return acc;
          }, {} as Record<string, Shelter>);
        }
      }

      // Combine updates with original shelter data
      return updates.map((u) => ({
        ...u,
        status: u.status as 'pending' | 'approved' | 'rejected',
        originalShelter: u.shelter_id ? sheltersMap[u.shelter_id] : undefined,
      })) as ShelterUpdateWithOriginal[];
    },
    enabled,
  });
};

export const useApproveUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (update: ShelterUpdate) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      if (update.shelter_id) {
        // IMPORTANT: Fetch the CURRENT state of the shelter from DB
        // This prevents race conditions when multiple updates are approved
        const { data: currentShelter, error: fetchError } = await supabase
          .from("shelters")
          .select("*")
          .eq("id", update.shelter_id)
          .single();

        if (fetchError) throw new Error(fetchError.message);

        // Convert to Shelter type
        const original: Shelter = {
          id: currentShelter.id,
          name: currentShelter.name,
          city: currentShelter.city,
          region: currentShelter.region,
          address: currentShelter.address,
          postal_code: currentShelter.postal_code,
          lat: currentShelter.lat,
          lng: currentShelter.lng,
          phone: currentShelter.phone,
          email: currentShelter.email,
          website: currentShelter.website,
          current_needs: currentShelter.current_needs,
          urgent_needs: currentShelter.urgent_needs,
          urgent_needs_set_at: currentShelter.urgent_needs_set_at,
          dog_walking_available: currentShelter.dog_walking_available,
          volunteering_available: currentShelter.volunteering_available,
          dogs_available: currentShelter.dogs_available,
          cats_available: currentShelter.cats_available,
          volunteer_info: currentShelter.volunteer_info,
          description: currentShelter.description,
          last_updated: currentShelter.last_updated ?? currentShelter.created_at,
          created_at: currentShelter.created_at,
          data_verified: currentShelter.data_verified ?? false,
          organization_verified: (currentShelter as any).organization_verified ?? false,
          organization_verified_at: (currentShelter as any).organization_verified_at ?? null,
          organization_verification_pending: (currentShelter as any).organization_verification_pending ?? false,
          organization_verification_requested_at: (currentShelter as any).organization_verification_requested_at ?? null,
          contacted: (currentShelter as any).contacted ?? false,
        };

        // Get only the fields that were intentionally changed in this submission.
        // If the submission was created with server-side diff (pending original_values.__changed_fields),
        // we use it as an allow-list to prevent overwriting other fields when approvals happen out-of-order.
        const allowUpdateKeys = getAllowUpdateKeysFromPending(update);
        const changedFields = getChangedFields(original, update, { allowUpdateKeys });

        console.log("[useApproveUpdate] Changed fields:", Object.keys(changedFields));

        // Store original values for the changed fields (for potential revert)
        const originalValues: Record<string, any> = {};
        for (const key of Object.keys(changedFields)) {
          originalValues[key] = (original as any)[key];
        }

        // Only update if there are actual changes
        if (Object.keys(changedFields).length > 0) {
          const { error: shelterError } = await supabase
            .from("shelters")
            .update({
              ...changedFields,
              last_updated: new Date().toISOString(),
              data_verified: true,
            })
            .eq("id", update.shelter_id);

          if (shelterError) throw new Error(shelterError.message);
        } else {
          // Even if no field changes, mark as verified
          const { error: shelterError } = await supabase
            .from("shelters")
            .update({
              last_updated: new Date().toISOString(),
              data_verified: true,
            })
            .eq("id", update.shelter_id);

          if (shelterError) throw new Error(shelterError.message);
        }

        // Mark update as approved with original values stored
        const { error: updateError } = await supabase
          .from("shelter_updates")
          .update({
            status: "approved",
            reviewed_at: new Date().toISOString(),
            reviewed_by: user.id,
            original_values: Object.keys(originalValues).length > 0 ? originalValues : null,
          })
          .eq("id", update.id);

        if (updateError) throw new Error(updateError.message);
      } else {
        // Create new shelter (from user submission = verified)
        const isOrgRequest = (update as any).is_organization_request === true;
        
        const { data: newShelter, error: shelterError } = await supabase
          .from("shelters")
          .insert({
            name: update.shelter_name,
            city: update.city,
            region: update.region,
            address: update.address,
            postal_code: update.postal_code,
            lat: update.lat,
            lng: update.lng,
            phone: update.phone,
            email: update.email,
            website: update.website,
            current_needs: update.current_needs,
            urgent_needs: update.urgent_needs,
            dog_walking_available: update.dog_walking_available,
            dogs_available: update.dogs_available,
            cats_available: update.cats_available,
            volunteering_available: update.volunteering_available,
            volunteer_info: update.volunteer_info,
            description: update.description,
            data_verified: true,
            // If organization request, set pending flag
            organization_verification_pending: isOrgRequest,
            organization_verification_requested_at: isOrgRequest ? new Date().toISOString() : null,
          })
          .select("id")
          .single();

        if (shelterError) throw new Error(shelterError.message);

        // Mark update as approved and link to newly created shelter
        const { error: updateError } = await supabase
          .from("shelter_updates")
          .update({
            status: "approved",
            reviewed_at: new Date().toISOString(),
            reviewed_by: user.id,
            shelter_id: newShelter.id, // Link to the new shelter for history tracking
          })
          .eq("id", update.id);

        if (updateError) throw new Error(updateError.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-updates"] });
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
    },
  });
};

export const useRejectUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      const { error } = await supabase
        .from("shelter_updates")
        .update({
          status: "rejected",
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq("id", updateId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-updates"] });
    },
  });
};

export const useRevertUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (update: ShelterUpdate) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nie jesteś zalogowany");

      if (!update.shelter_id) {
        throw new Error("Nie można cofnąć zmiany dla nowego schroniska");
      }

      if (!update.original_values || Object.keys(update.original_values).length === 0) {
        throw new Error("Brak zapisanych oryginalnych wartości do przywrócenia");
      }

      // Restore original values to the shelter
      const { error: shelterError } = await supabase
        .from("shelters")
        .update({
          ...update.original_values,
          last_updated: new Date().toISOString(),
        })
        .eq("id", update.shelter_id);

      if (shelterError) throw new Error(shelterError.message);

      // Mark the update as reverted - clear original_values so it can't be reverted again
      const { error: updateError } = await supabase
        .from("shelter_updates")
        .update({
          status: "rejected",
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          original_values: null, // Clear to prevent re-revert
        })
        .eq("id", update.id);

      if (updateError) throw new Error(updateError.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shelter-updates"] });
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-history"] });
    },
  });
};
